import { useCallback } from "react";
import { useAppShell } from "@salestrak/ui";

/**
 * True when the desktop sidebar is in its rail-only collapsed state.
 * On mobile, the drawer being dismissed is not "collapsed" — the pane is
 * simply off-screen — so this always returns false there.
 */
export function useSidebarCollapsed(): boolean {
  const { isMobile, state } = useAppShell();
  return !isMobile && state === "collapsed";
}

/**
 * Re-opens the collapsed desktop sidebar pane. Paired with
 * `useSidebarCollapsed` — the module rail renders an expand control only
 * while that reads true, and this is what the control calls.
 *
 * Both hooks exist so shell state reaches components through ONE app-side
 * seam rather than every caller reaching into `useAppShell()` itself.
 */
export function useExpandSidebar(): () => void {
  const { setOpen } = useAppShell();
  return useCallback(() => setOpen(true), [setOpen]);
}

/**
 * Collapses the desktop sidebar pane to the rail. The module-menu header's
 * chevrons-left control calls this; the rail's expand control is its inverse.
 */
export function useCollapseSidebar(): () => void {
  const { setOpen } = useAppShell();
  return useCallback(() => setOpen(false), [setOpen]);
}
